import { createFileRoute, Link, useSearch } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { FormattedMessage } from "react-intl";
import { Button } from "@/components/ui/button";
import { ExerciseLayout } from "@/components/ui/exercise-layout";
import { useDiaryStore } from "@/lib/stores/diaryStore";
import { useAuthStore } from "@/lib/stores/authStore";
import { useLocale } from "@/contexts/IntlContext";
import ConfirmationModal from "@/components/ui/confirmation-modal";
import type { DiaryEntry } from "@/lib/types/diary";
import { FiEdit2, FiTrash2 } from "react-icons/fi";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa6";
import happyIcon from "@/assets/icons/happy-icon1.svg";
import sadIcon from "@/assets/icons/sad-icon1.svg";
import cherfulIcon from "@/assets/icons/cherful-icon1.svg";
import angryIcon from "@/assets/icons/angry-icon1.svg";
import boredIcon from "@/assets/icons/bored-icon1.svg";
import neutralIcon from "@/assets/icons/neutral-icon.png";

export const Route = createFileRoute(
  "/(content)/adults/exercises/(shared)/diary-adults/archive"
)({
  component: RouteComponent,
  head: () => ({ meta: [{ title: "My Diary Archive - HAMD" }] }),
  beforeLoad: () => ({
    title: "weakness.exercises.diary.archive",
    navbar: {
      showTitle: true,
    },
  }),
  validateSearch: (search: Record<string, unknown>) => ({
    saved: search.saved === true,
  }),
});

const feelings = [
  { id: "happy", icon: happyIcon, label: "Happy" },
  { id: "cheerful", icon: cherfulIcon, label: "Cheerful" },
  { id: "sad", icon: sadIcon, label: "Sad" },
  { id: "angry", icon: angryIcon, label: "Angry" },
  { id: "bored", icon: boredIcon, label: "Bored" },
];

const getFeeling = (id?: string) =>
  feelings.find((f) => f.id === id) ?? {
    id: "neutral",
    icon: neutralIcon,
    label: "Neutral",
    color: "#9CA3AF",
  };

function RouteComponent() {
  const { saved } = useSearch({
    from: "/(content)/adults/exercises/(shared)/diary-adults/archive",
  });

  const [showSuccess, setShowSuccess] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<DiaryEntry | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState("");
  const [editFeeling, setEditFeeling] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState<DiaryEntry | null>(null);
  const [currentDate, setCurrentDate] = useState(new Date());

  const {
    entries,
    isLoading,
    error,
    fetchEntries,
    deleteEntry,
    updateEntry,
    clearError,
    clearEntries,
  } = useDiaryStore();
  const { user } = useAuthStore();
  const { locale } = useLocale();

  useEffect(() => {
    if (!user) {
      clearEntries();
      return;
    }
    fetchEntries().catch(console.error);
  }, [fetchEntries, clearEntries, user]);

  useEffect(() => {
    if (saved) {
      setShowSuccess(true);
      const t = setTimeout(() => setShowSuccess(false), 3000);
      return () => clearTimeout(t);
    }
  }, [saved]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const localeString = locale === "ar" ? "ar" : "en-US";

    const month = date.toLocaleDateString(localeString, { month: "long" });
    const day = date.getDate();
    const year = date.getFullYear();

    return `${day} ${month} ${year}`;
  };

  const getCurrentMonthName = () => {
    const localeString = locale === "ar" ? "ar" : "en-US";

    return currentDate.toLocaleDateString(localeString, {
      month: "long",
      year: "numeric",
    });
  };

  const goToPreviousMonth = () => {
    setCurrentDate((prevDate) => {
      const newDate = new Date(prevDate);
      newDate.setMonth(newDate.getMonth() - 1);
      return newDate;
    });
  };

  const goToNextMonth = () => {
    setCurrentDate((prevDate) => {
      const newDate = new Date(prevDate);
      newDate.setMonth(newDate.getMonth() + 1);
      return newDate;
    });
  };

  const isCurrentMonth = () => {
    const now = new Date();
    return (
      currentDate.getMonth() === now.getMonth() &&
      currentDate.getFullYear() === now.getFullYear()
    );
  };

  const filteredEntries = entries.filter((entry) => {
    const entryDate = new Date(entry.createdAt);
    return (
      entryDate.getMonth() === currentDate.getMonth() &&
      entryDate.getFullYear() === currentDate.getFullYear()
    );
  });

  const handleEdit = (entry: DiaryEntry) => {
    setSelectedEntry(entry);
    setEditContent(entry.content);
    setEditFeeling(entry.feeling || null);
    setIsEditing(true);
  };

  const handleSaveEdit = async () => {
    if (selectedEntry && editContent.trim()) {
      try {
        clearError();
        await updateEntry(
          selectedEntry.id,
          editContent,
          editFeeling || undefined
        );
        setIsEditing(false);
        setSelectedEntry(null);
        setEditContent("");
        setEditFeeling(null);
      } catch (err) {
        console.error("Error updating entry:", err);
      }
    }
  };

  const handleDelete = (entry: DiaryEntry) => {
    setEntryToDelete(entry);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (entryToDelete) {
      try {
        clearError();
        await deleteEntry(entryToDelete.id);
        if (selectedEntry?.id === entryToDelete.id) {
          setSelectedEntry(null);
          setIsEditing(false);
        }
      } catch (err) {
        console.error("Error deleting entry:", err);
      }
    }
    setEntryToDelete(null);
    setShowDeleteModal(false);
  };

  if (isLoading && entries.length === 0) {
    return (
      <div className="px-4 py-6 text-center text-gray-600">
        <FormattedMessage id="diary.archive.loading" />
      </div>
    );
  }

  return (
    <ExerciseLayout
      contentClassName="py-7.5 space-y-9.5"
      actions={
        isCurrentMonth() ? (
          <Link
            to="/adults/exercises/diary-adults/exercise"
            search={{ templateId: undefined }}
          >
            <Button className="w-full">
              <FormattedMessage
                id="diary.archive.addMemories"
                defaultMessage="Add Memories"
              />
            </Button>
          </Link>
        ) : null
      }
    >
      <div className="flex justify-between items-center bg-taupe p-2.5 rounded-[12px]">
        <button
          onClick={goToPreviousMonth}
          className="text-white w-5 h-5 cursor-pointer"
          aria-label="Previous month"
        >
          {locale === "ar" ? (
            <FaChevronRight className="w-full h-full" />
          ) : (
            <FaChevronLeft className="w-full h-full" />
          )}
        </button>
        <h3 className="text-white text-center ">{getCurrentMonthName()}</h3>
        <button
          onClick={goToNextMonth}
          className="text-white w-5 h-5 cursor-pointer"
          aria-label="Next month"
        >
          {locale === "ar" ? (
            <FaChevronLeft className="w-full h-full" />
          ) : (
            <FaChevronRight className="w-full h-full" />
          )}
        </button>
      </div>

      {showSuccess && (
        <div className="mb-4 p-3 bg-primary text-white text-small rounded-lg">
          <FormattedMessage id="diary.archive.successMessage" />
        </div>
      )}
      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 text-small rounded-lg">
          {error}
        </div>
      )}
      <div className="space-y-2.5">
        {filteredEntries.length === 0 ? (
          <div className="text-center py-8 text-gray">
            <FormattedMessage
              id="diary.archive.noEntriesForMonth"
              defaultMessage="No diary entries for {monthName}"
              values={{ monthName: getCurrentMonthName() }}
            />
          </div>
        ) : (
          filteredEntries.map((entry) => {
            const feeling = getFeeling(entry.feeling);
            return (
              <div
                key={entry.id}
                className="bg-accent rounded-xl py-2 px-[17px] relative"
              >
                <div className="flex justify-between items-end pb-1">
                  <span className="text-micro text-primary">
                    {formatDate(entry.createdAt)}
                  </span>

                  <div className="w-8.5 h-8.5 rounded-full flex items-center justify-center bg-background">
                    {feeling.icon ? (
                      <img
                        src={feeling.icon}
                        alt={feeling.label}
                        className="w-6 h-6"
                        draggable={false}
                      />
                    ) : (
                      <div className="w-7 h-7 rounded-full bg-gray-300" />
                    )}
                  </div>
                </div>

                <div className="space-y-1">
                  <h3 className="font-medium">
                    <FormattedMessage
                      id="diary.archive.dearDiary"
                      defaultMessage="Dear Diary,"
                    />
                  </h3>
                  <div className="h-1.5 w-19 rounded-full bg-primary" />
                </div>

                {isEditing && selectedEntry?.id === entry.id ? (
                  <div className="space-y-3 py-2">
                    <textarea
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      className="w-full min-h-[100px] p-3  rounded-md text-small focus:outline-none focus:ring-2 focus:ring-primary bg-white"
                      disabled={isLoading}
                      rows={4}
                    />
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={handleSaveEdit}
                        disabled={isLoading}
                        className="flex items-center gap-1 text-micro w-15"
                      >
                        <FormattedMessage id="diary.archive.save" />
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        className="flex items-center gap-1 text-micro w-15"
                        onClick={() => {
                          setIsEditing(false);
                          setSelectedEntry(null);
                          setEditContent("");
                          setEditFeeling(null);
                        }}
                        disabled={isLoading}
                      >
                        <FormattedMessage id="diary.archive.cancel" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="text-small text-dark-gray whitespace-pre-wrap break-words py-2">
                      {entry.content.length > 150 ? (
                        <>
                          {selectedEntry?.id === entry.id
                            ? entry.content
                            : entry.content.substring(0, 150) + "..."}
                          {selectedEntry?.id !== entry.id && (
                            <button
                              onClick={() => setSelectedEntry(entry)}
                              className="text-primary ml-1"
                            >
                              <FormattedMessage id="diary.archive.readMore" />
                            </button>
                          )}
                          {selectedEntry?.id === entry.id && (
                            <button
                              onClick={() => setSelectedEntry(null)}
                              className="text-primary ml-1"
                            >
                              <FormattedMessage id="diary.archive.showLess" />
                            </button>
                          )}
                        </>
                      ) : (
                        entry.content
                      )}
                    </div>

                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => handleEdit(entry)}
                        disabled={isEditing || isLoading}
                        className="w-6 h-6 rounded-md flex items-center justify-center hover:opacity-80 transition-opacity bg-background"
                        aria-label="Edit"
                      >
                        <FiEdit2 className="text-primary" size={15} />
                      </button>
                      <button
                        onClick={() => handleDelete(entry)}
                        disabled={isLoading}
                        className="w-6 h-6 rounded-md flex items-center justify-center hover:opacity-80 transition-opacity bg-background"
                        aria-label="Delete"
                      >
                        <FiTrash2 className="text-primary" size={15} />
                      </button>
                    </div>
                  </>
                )}
              </div>
            );
          })
        )}
      </div>

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        titleKey="diary.archive.delete"
        messageKey="diary.archive.deleteConfirm"
        confirmButtonTextKey="diary.archive.delete"
        cancelButtonTextKey="common.cancel"
      />
    </ExerciseLayout>
  );
}
